import React, { useState } from "react";
import {
  KeyboardAvoidingView,
  ScrollView,
  StyleSheet,
  View,
} from "react-native";

import {
  Button,
  BUTTON_STATUS,
  SIZE,
  Input,
  BORDER_STYLES,
  TEXT_STYLES,
  TYPOGRAPHY,
  VARIANTS_STYLES,
} from "@dehaat/dds";
import { BG_STYLES, COMMON_STYLES } from "@dehaat/dds";
import fetchOrCreateUser from "../apis/fetchOrCreateUser";
import { useAppDispatch, useAppSelector } from "app/hooks/reduxHooks";
import {
  selectFarmerName,
  selectFarmerPhone,
  setFarmerId,
  setFarmerName,
  setFarmerPhone,
} from "app/farmer360/store/app";
import ImageSourceDialog from "../components/modals/ImageSourceDialog";
import useImageUpload from "../hooks/useImageUpload";
import {
  AddFarmerScreenProps,
  ROUTES as FARMER_360_ROUTES,
} from "app/farmer360/routes/types";
import { CommonActions } from "@react-navigation/native";
import { trackEvent } from "app/farmer360/utils/tracking";
import { CustomEventTypes } from "../utils/events/CustomEventTypes";
import ToastAndroid from "app/mocks/ToastAndroid";
import { useTranslation } from "react-i18next";
import AddCropModal from "app/farmer360/KhetEdge/components/crop/AddCropModal";

const AddFarmer = ({ navigation }: AddFarmerScreenProps) => {
  const dispatch = useAppDispatch();
  const [showDialog, setShowDialog] = useState(false);
  const routes = navigation.getState().routes;
  const firstRoute = routes[0];
  const secondRoute = routes[1];
  const farmerName = useAppSelector(selectFarmerName);
  const farmerPhone = useAppSelector(selectFarmerPhone);
  const [name, setName] = useState(farmerName);
  const [phone, setPhone] = useState(farmerPhone);
  const [showCropSelector, setShowCropSelector] = useState(false);
  const [cropId, setCropId] = useState<string>("");
  const isValidName = name?.trim()?.length > 0;
  const isValidPhone = phone?.length === 10;
  const isFormValid = isValidName && isValidPhone;
  const { t } = useTranslation();

  const handleSave = async () => {
    if (!isFormValid) {
      if (!isValidName) {
        ToastAndroid.show("Please enter a valid name.", ToastAndroid.SHORT);
      } else if (!isValidPhone) {
        ToastAndroid.show(
          "Please enter a valid phone number.",
          ToastAndroid.SHORT,
        );
      }
      return;
    }
    trackEvent(CustomEventTypes.CropDocImageFarmerAdded, {
      UserType: "other farmer",
      farmername: name,
      farmermobile: phone,
    });
    try {
      const response = await fetchOrCreateUser(phone, name);
      if (!response) {
        ToastAndroid.show("Please enter valid details.", ToastAndroid.SHORT);
        return;
      } else {
        dispatch(setFarmerId(response.id));
        dispatch(setFarmerName(response.full_name));
        dispatch(setFarmerPhone(response.phone_number));
      }
      setShowCropSelector(true);
    } catch (err) {
      console.error("handleSave error:", err);
    }
  };

  const { uploadFromCamera, uploadFromGallery } = useImageUpload(() => {
    navigation.dispatch(
      CommonActions.reset({
        index: 1,
        routes: [
          { name: firstRoute.name },
          { name: secondRoute.name },
          {
            name: FARMER_360_ROUTES.START_DIAGNOSIS,
            params: { cropId: cropId },
          },
        ],
      }),
    );
  });

  const validateMobileNumber = (num: string) => num?.length === 10;

  return (
    <KeyboardAvoidingView style={[COMMON_STYLES.flex1, BG_STYLES.bgWhite]}>
      <ScrollView
        contentContainerStyle={[COMMON_STYLES.p1, COMMON_STYLES.flexGrow]}
        keyboardShouldPersistTaps="handled"
      >
        <Input
          label={t("enter_farmer_name")}
          value={name}
          onChangeText={(text) => setName(text)}
          variant="outlined"
          containerStyle={[
            COMMON_STYLES.pv1,
            COMMON_STYLES.ph1,
            BG_STYLES.bgWhite,
            COMMON_STYLES.b1,
            BORDER_STYLES.borderNeutral20,
          ]}
          variantTextStyle={styles.labelStyle}
        />

        <Input
          label={t("phone_number")}
          value={phone}
          keyboardType="numeric"
          maxLength={10}
          onChangeText={(text) => {
            if (/^\d*$/.test(text)) setPhone(text);
          }}
          variant="outlined"
          containerStyle={[
            COMMON_STYLES.pv1,
            COMMON_STYLES.ph1,
            BG_STYLES.bgWhite,
            COMMON_STYLES.b1,
            BORDER_STYLES.borderNeutral20,
          ]}
          variantTextStyle={styles.labelStyle}
        />
      </ScrollView>

      <View style={[COMMON_STYLES.p1, BG_STYLES.bgWhite, styles.buttonWrapper]}>
        <Button
          text={t("save_and_proceed")}
          onClick={handleSave}
          size={SIZE.large}
          status={
            validateMobileNumber(phone) && isValidName
              ? BUTTON_STATUS.ACTIVE
              : BUTTON_STATUS.DISABLED
          }
        />
      </View>
      {showDialog && (
        <ImageSourceDialog
          visible={showDialog}
          onClose={() => setShowDialog(false)}
          uploadFromCamera={() => {
            trackEvent(CustomEventTypes.CropDocImageSourceSelected, {
              selectiontype: "camera",
            });
            setShowDialog(false);
            uploadFromCamera();
          }}
          uploadFromGallery={() => {
            trackEvent(CustomEventTypes.CropDocImageSourceSelected, {
              selectiontype: "gallery",
            });
            setShowDialog(false);
            uploadFromGallery();
          }}
        />
      )}
      {showCropSelector && (
        <AddCropModal
          onSubmit={(crop) => {
            if (crop?.id) {
              setCropId(crop.id);
              setShowCropSelector(false);
              setShowDialog(true);
            }
          }}
          onClose={() => setShowCropSelector(false)}
        />
      )}
    </KeyboardAvoidingView>
  );
};

const styles = StyleSheet.create({
  buttonWrapper: {
    shadowColor: "#000",
    shadowOffset: { width: 0, height: -2 },
    shadowOpacity: 0.1,
    shadowRadius: 6,
    elevation: 10,
  },
  labelStyle: {
    ...TEXT_STYLES.textNeutral80,
    ...TYPOGRAPHY.CAPTION1,
    ...VARIANTS_STYLES.BASE,
  },
});

export default AddFarmer;
